import React, { useState, useMemo } from 'react';
import { registrarPadre } from '../services/matriculaService';
import { Users, Save, X } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Select from 'react-select';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input/input';
import es from 'react-phone-number-input/locale/es.json';

// Función para formatear nombres y apellidos (primera letra mayúscula, resto minúsculas)
const formatearNombre = (texto) => {
  if (!texto) return '';
  return texto
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map(palabra => palabra.charAt(0).toUpperCase() + palabra.slice(1))
    .join(' ');
};

// Función para validar que el nombre completo tenga al menos un nombre y un apellido
const validarNombreCompleto = (nombreCompleto) => {
  if (!nombreCompleto) return false;
  const partes = nombreCompleto.trim().split(/\s+/);
  return partes.length >= 2 && partes.every(parte => parte.length > 0);
};

// Función para obtener el emoji de la bandera del país
const getFlagEmoji = (countryCode) => {
  if (!countryCode) return '';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

const Button = ({ 
  children, 
  onClick, 
  className = '', 
  variant = 'primary', 
  size = 'default',
  type
}) => {
  const baseStyles = 'rounded-md font-medium transition-all duration-200 flex items-center justify-center shadow-sm';
  
  const variantStyles = {
    primary: 'bg-indigo-500 text-white hover:bg-indigo-600',
    ghost: 'bg-transparent text-gray-600 hover:bg-gray-50',
    green: 'bg-emerald-500 text-white hover:bg-emerald-600',
    danger: 'bg-rose-500 text-white hover:bg-rose-600'
  };

  const sizeStyles = {
    default: 'px-3 py-1.5 text-sm',
    sm: 'px-2 py-1 text-xs',
    lg: 'px-4 py-2 text-base',
    icon: 'p-1.5',
    xs: 'p-0.5 text-xs'
  };

  return (
    <button
      type={type}
      onClick={onClick}
      className={`
        ${baseStyles} 
        ${variantStyles[variant]} 
        ${sizeStyles[size]} 
        ${className}
      `}
    >
      {children}
    </button>
  );
};

const RegistrarPadre = ({ onClose }) => {
  const [padreData, setPadreData] = useState({
    name: '',
    email: '',
    password_us: '',
    cedula: '',
    lugar_trabajo: '',
    cargo: '',
    paisDeOrigen: '', 
    telefono: '',
  });

  const [loading, setLoading] = useState(false);
  const [errores, setErrores] = useState({});

  // Opciones de países para el selector
  const countryOptions = useMemo(() => {
    try {
      return getCountries().map(country => ({
        value: country,
        label: es[country] || country,  // Usamos la localización en español
        flag: getFlagEmoji(country),
        code: country,
        callingCode: `+${getCountryCallingCode(country)}`
      }));
    } catch (error) {
      console.error('Error al cargar los países:', error);
      return [];
    }
  }, []);
  
  // Manejar cambio de país para el campo de nacionalidad
  const handleNacionalidadChange = (selectedOption) => {
    if (selectedOption) {
      setPadreData(prev => ({
        ...prev,
        paisDeOrigen: selectedOption.value
      }));
      
      // Limpiar el error de nacionalidad si existe
      if (errores.paisDeOrigen) {
        setErrores(prev => ({
          ...prev,
          paisDeOrigen: null
        }));
      }
    }
  };
  
  // Obtener el valor actual del país seleccionado
  const getSelectedCountry = () => {
    if (!padreData.paisDeOrigen) return null;
    return countryOptions.find(option => option.value === padreData.paisDeOrigen);
  };

  const manejarCambio = (e) => {
    const { name, value, type, checked } = e.target;
    setPadreData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Limpiar el error del campo cuando el usuario comienza a escribir
    if (errores[name]) {
      setErrores(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleCountrySelect = (countryCode) => {
    setPadreData((prev) => ({
      ...prev,
      paisDeOrigen: countryCode,
    }));
    // Clear any previous error for this field when a selection is made
    if (errores.paisDeOrigen) {
      setErrores(prevErrores => ({ ...prevErrores, paisDeOrigen: null }));
    }
  };

  const validateCedula = (cedula) => {
    if (!/^\d{10}$/.test(cedula)) {
      return 'La cédula debe tener 10 dígitos';
    }

    const codigoProvincia = parseInt(cedula.slice(0, 2));
    if (codigoProvincia < 1 || codigoProvincia > 24) {
      return 'El código de provincia no es válido';
    }

    const digitoVerificador = parseInt(cedula.slice(-1));
    const coeficientes = [2, 1, 2, 1, 2, 1, 2, 1, 2];
    let suma = 0;
    for (let i = 0; i < 9; i++) {
      let resultado = parseInt(cedula[i]) * coeficientes[i];
      suma += resultado > 9 ? resultado - 9 : resultado;
    }

    let digitoCalculado = 10 - (suma % 10);
    if (digitoCalculado === 10) {
      digitoCalculado = 0;
    }

    return digitoCalculado === digitoVerificador ? null : 'La cédula es inválida';
  };

  const validateEmail = (email) => {
    const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return regex.test(email) ? null : 'El correo electrónico no es válido';
  };

  const validateTelefono = (telefono) => {
    const regex = /^[0-9]{10}$/;
    return regex.test(telefono) ? null : 'El teléfono debe tener 10 dígitos';
  };

  const manejarSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrores({});

    // Validar que el nombre completo tenga al menos un nombre y un apellido
    if (!validarNombreCompleto(padreData.name)) {
      setErrores(prev => ({
        ...prev,
        name: 'Debe ingresar al menos un nombre y un apellido'
      }));
      setLoading(false);
      return;
    }

    // Formatear nombres y apellidos y asegurar que los campos opcionales estén definidos
    const datosFormateados = {
      ...padreData,
      name: formatearNombre(padreData.name),
      nacionalidad: padreData.paisDeOrigen, // Mapear paisDeOrigen a nacionalidad
      lugar_trabajo: padreData.lugar_trabajo || '', // Asegurar que sea string vacío si es undefined
      cargo: padreData.cargo || '' // Asegurar que sea string vacío si es undefined
    };

    // Default password to cedula if not provided
    if (!datosFormateados.password_us) {
      datosFormateados.password_us = datosFormateados.cedula;
    }

    const erroresValidacion = {};

    if (!datosFormateados.name) erroresValidacion.name = 'El nombre es obligatorio';
    if (!datosFormateados.email) erroresValidacion.email = 'El correo electrónico es obligatorio';
    if (!datosFormateados.cedula) erroresValidacion.cedula = 'La cédula es obligatoria';
    if (!datosFormateados.paisDeOrigen) erroresValidacion.paisDeOrigen = 'El país de origen es obligatorio'; 
    if (!datosFormateados.telefono) erroresValidacion.telefono = 'El teléfono es obligatorio';

    const cedulaError = validateCedula(datosFormateados.cedula);
    if (cedulaError) erroresValidacion.cedula = cedulaError;

    const emailError = validateEmail(datosFormateados.email);
    if (emailError) erroresValidacion.email = emailError;

    const telefonoError = validateTelefono(datosFormateados.telefono);
    if (telefonoError) erroresValidacion.telefono = telefonoError;

    if (Object.keys(erroresValidacion).length > 0) {
      setErrores(erroresValidacion);
      setLoading(false);
      return;
    }

    try {
      await registrarPadre(datosFormateados);
      toast.success('Padre registrado exitosamente', {
        position: 'top-right',
        autoClose: 3000,

        style: { 
          marginTop: '40px',
          zIndex: 9999,
          fontFamily: '"Exo 2", sans-serif',
          fontWeight: 500
        }
      });
      setTimeout(onClose, 1000);
    } catch (error) {
      if (error.message.includes('cédula ingresada')) {
        toast.error('Ya existe un usuario registrado con esta cédula', {
          position: 'top-right',
          autoClose: 3000,
style: { 
  marginTop: '40px',
  zIndex: 9999,
  fontFamily: '"Exo 2", sans-serif',
  fontWeight: 500
}
        });
      } else if (error.message.includes('correo electrónico ingresado')) {
        toast.error('El correo electrónico ya está siendo utilizado', {
          position: 'top-right',
          autoClose: 3000,
          style: { 
            marginTop: '40px',
            zIndex: 9999,
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
          }
        });
      } else {
        toast.error('Error al registrar el usuario', {
          position: 'top-right',
          autoClose: 3000,
          style: { 
            marginTop: '40px',
            zIndex: 9999,
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
          }
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-ful">
      <ToastContainer 
        position="top-right"
        autoClose={3000}
      />
      
      <form onSubmit={manejarSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          {[
            { 
              name: 'name', 
              label: 'Nombre', 
              placeholder: 'Ingrese nombre y apellido',

              required: true
            },
            { 
              name: 'email', 
              label: 'Correo Electrónico', 
              placeholder: 'ejemplo@correo.com', 
              required: true 
            },
            { 
              name: 'cedula', 
              label: 'Cédula', 
              placeholder: '1234567890', 
              required: true 
            },
            { 
              name: 'lugar_trabajo', 
              label: 'Lugar de Trabajo', 
              placeholder: 'Nombre de la empresa (opcional)' 
            },
            { 
              name: 'cargo', 
              label: 'Cargo', 
              placeholder: 'Cargo actual (opcional)', 
              type: 'text' 
            },
            { 
              label: 'País de Origen', 
              name: 'paisDeOrigen', 
              type: 'country', 
              required: true 
            },
            { 
              name: 'telefono', 
              label: 'Teléfono', 
              placeholder: '0987654321', 
              type: 'tel', 
              required: true 
            }
          ].map((field) => (
            <div key={field.name}>
              <label className="block text-xs font-medium text-gray-700 mb-1 bg-white">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {field.type === 'country' ? (
                <div className="w-full">
                  <Select
                    options={countryOptions}
                    value={getSelectedCountry()}
                    onChange={handleNacionalidadChange}
                    placeholder="Seleccionar país"
                    className="w-full"
                    classNamePrefix="select"
                    isSearchable
                    noOptionsMessage={() => 'País no encontrado'}
                    formatOptionLabel={({ label, flag }) => (
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">{flag}</span>
                        <span>{label}</span>
                      </div>
                    )}
                    styles={{
                      control: (base, { isFocused }) => ({
                        ...base,
                        minHeight: '40px',
                        borderColor: errores.paisDeOrigen 
                          ? '#ef4444' 
                          : isFocused ? '#3b82f6' : '#d1d5db',
                        boxShadow: isFocused ? '0 0 0 1px #3b82f6' : 'none',
                        '&:hover': {
                          borderColor: errores.paisDeOrigen ? '#dc2626' : '#9ca3af'
                        },
                        backgroundColor: '#fff',
                        borderRadius: '0.375rem',
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 50,
                        backgroundColor: '#fff',
                        borderRadius: '0.375rem',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
                      }),
                      option: (base, { isFocused, isSelected }) => ({
                        ...base,
                        backgroundColor: isSelected 
                          ? '#3b82f6' 
                          : isFocused 
                            ? '#f3f4f6' 
                            : '#fff',
                        color: isSelected ? '#fff' : '#1f2937',
                        '&:active': {
                          backgroundColor: isSelected ? '#3b82f6' : '#e5e7eb'
                        }
                      }),
                      input: (base) => ({
                        ...base,
                        color: '#1f2937',
                        'input': {
                          boxShadow: 'none !important'
                        }
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: '#1f2937'
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: '#9ca3af'
                      })
                    }}
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary: '#3b82f6',
                        primary25: '#e0f2fe',
                        primary50: '#bae6fd',
                        neutral0: '#fff',
                        neutral80: '#1f2937',
                      },
                    })}
                  />
                  {errores.nacionalidad && (
                    <p className="mt-1 text-sm text-red-600">{errores.nacionalidad}</p>
                  )}
                </div>
              ) : (
                <input
                  type={field.type || 'text'}
                  name={field.name}
                  id={field.name}
                  value={padreData[field.name]}
                  onChange={manejarCambio}
                  placeholder={field.placeholder} // Added placeholder back
                  className={`w-full px-2.5 py-1.5 text-sm border bg-white ${errores[field.name] ? 'border-rose-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-indigo-500 bg-white focus:border-indigo-500`}
                />
              )}
              {field.name === 'name' && (
                <p className={`text-xs mt-1 bg-white ${errores.name ? 'text-red-600' : 'text-gray-500'}`}>
                  {errores.name || (!validarNombreCompleto(padreData.name) ? 'Debe incluir al menos un nombre y un apellido' : '')}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
          >
            <X className="mr-1.5" size={14} />
            Cancelar
          </Button>
          <Button 
            type="submit"
            variant="primary"
            size="sm"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                Registrando...
              </>
            ) : (
              <>
                <Save className="mr-1.5" size={14} />
                Registrar
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default RegistrarPadre;
